<script setup lang="ts">
import { ref } from 'vue';
import apiClient from '@src/api/api-client.js';
import { closeLoader, openLoader } from '@utils/loader-util.js';
import { Bin } from '@src/warehouse/interfaces/bin.js';
import { getTotalStockByBin } from '@src/warehouse/util/bin-util.js';
import { useSingleRefComposable } from '@src/application/composables/single-ref-composable.js';
import { useExpressPickComposable } from '@src/express-pick/express-pick-composable.js';
import ClusterPicklist from '@src/pick/cluster-picklist/cluster-picklist.vue';
import { ClusterMinimal } from '../cluster/interfaces/cluster-minimal.ts';
import PickedProducts from '@src/express-pick/picked-products.vue';
import { Picked } from '@src/pick/cluster-picklist/picked.ts';
import { PicklistLine } from '@src/pick/cluster-picklist/picklist-line.ts';
import VerticalAlignChildrenWrapper from '@components/layout-wrapper/vertical-align-children-wrapper.vue';
import Headline from '@components/headline.vue';
import HorizontalAlignChildrenWrapper from '@components/layout-wrapper/horizontal-align-children-wrapper.vue';

const { unassignExpressPickQueues, expressPickUser, toPick, loadToPick } = useExpressPickComposable();

const currentUserBin = ref<Bin|null>((await apiClient.get('/be/bin/current-user')).data);
const currentUserBinTotalStock = ref<number|null>(await getTotalStockByBin(currentUserBin.value as Bin));

async function loadExpressPickClusters() {
    const request = apiClient.get('/be/express-pick/clusters');
    request.disableErrorModal();
    const clusters = (await request).data;
    currentUserBinTotalStock.value = await getTotalStockByBin(currentUserBin.value as Bin);
    return clusters;
}

async function loadPicklistLines(cluster: ClusterMinimal|null) {
    return (await apiClient.get('/be/express-pick/assigned/' + (cluster !== null ? cluster.id : ''))).data;
}

const emit = defineEmits<{
    (event: 'done'): void
}>();

async function unassign() {
    await unassignExpressPickQueues();
    emit('done');
}

async function stockTransfer() {
    openLoader();
    await apiClient.post('/be/express-pick/stock-transfer');
    closeLoader();
    emit('done');
}

async function pick(picked: Picked) {
    openLoader();
    await apiClient.post('/be/express-pick/pick', picked);
    await getPickedProductsRef()
        .load();
    await loadToPick();
    closeLoader();
}

async function pickSerial(serial: string) {
    openLoader();
    await apiClient.post('/be/express-pick/pick-serial/' + encodeURIComponent(serial));
    await getPickedProductsRef()
        .load();
    await loadToPick();
    closeLoader();
}

async function notFound(picklistLine: PicklistLine) {
    openLoader();
    await apiClient.post('/be/express-pick/not-found', {
        product: picklistLine.product,
        bin: picklistLine.bin,
    });
    await loadToPick();
    closeLoader();
}

const { setSingleTemplateRef: setPickedProductsRef, getSingleTemplateRef: getPickedProductsRef } = useSingleRefComposable();

loadToPick();
</script>

<template>
    <VerticalAlignChildrenWrapper>
        <HorizontalAlignChildrenWrapper alignment="center">
            <Headline type="h2">Expreeeeess {{ expressPickUser?.currentDestinationBin?.whs.whsCode }} ({{ toPick }})</Headline>
        </HorizontalAlignChildrenWrapper>
        <ClusterPicklist
            :load-clusters="loadExpressPickClusters"
            :load-picklist-lines="loadPicklistLines"
            :on-pick="pick"
            :on-pick-serial="pickSerial"
            :on-not-found="notFound"
            enable-abort
            enable-finish
            @abort="unassign"
            @finish="stockTransfer"
        >
            <template v-slot:picked-products>
                <PickedProducts :ref="setPickedProductsRef"/>
            </template>
        </ClusterPicklist>
    </VerticalAlignChildrenWrapper>
</template>
